import {
  AIEndpointContext,
  getAIEndpointResolved,
  isAIRegionRoutingEnabled,
  ResolvedAIEndpoint,
} from "./resolveAIEndpoint";
import { getAIEndpointVuexStore } from "./aiEndpointStoreRef";
import {
  COUNTRY_TO_CONTINENT,
  normalizeRegionCode,
} from "./regionMaps";
import { withSentryBrowser } from "./sentryClient";

/** TODO(temp): set false or remove when regional routing QA is done. */
export const AI_ROUTING_DRY_RUN = false;

function logDryRun(source: string, detail: Record<string, unknown> = {}): void {
  if (!AI_ROUTING_DRY_RUN) return;
  console.info(`[AI routing] ${source}`, {
    ...detail,
    t: new Date().toISOString(),
  });
}

if (AI_ROUTING_DRY_RUN) {
  console.warn(
    "[AI routing] DRY RUN — AI HTTP blocked (AI_ROUTING_DRY_RUN in getAIEndpoint.ts)"
  );
}

export type { AIEndpointContext, ResolvedAIEndpoint };

/** ISO country on user/team docs; `region` is legacy Firebase field name. */
export function readStoredCountry(
  doc: { country?: string | null; region?: string | null } | null | undefined
): string | null {
  const raw = doc?.country ?? doc?.region ?? null;
  if (raw == null || String(raw).trim() === "") return null;
  return String(raw).trim();
}

export interface AIEndpointCountryLookupDebug {
  routingEnabled: boolean;
  user: {
    email: string | null;
    countryFromProfile: string | null;
    countryNormalized: string | null;
  };
  team: {
    teamId: string | null;
    countryFromTeam: string | null;
    countryNormalized: string | null;
  };
  /** user.country → team.country → default "us" */
  effectiveCountry: {
    code: string;
    pickedFrom: "user.country" | "team.country" | "default";
    continent: string | null;
  };
  endpoint: ResolvedAIEndpoint;
}

export function getAIEndpointCountryLookupDebug(rootState: {
  user?: {
    user?: {
      email?: string;
      teamId?: string;
      country?: string | null;
      region?: string | null;
    };
  };
  team?: {
    team?: { _id?: string; country?: string | null; region?: string | null };
  };
}): AIEndpointCountryLookupDebug {
  const context = aiEndpointContextFromRootState(rootState);
  const userCountryRaw = context.userCountryCode ?? null;
  const teamCountryRaw = context.teamCountryCode ?? null;
  const userCountryNormalized = normalizeRegionCode(userCountryRaw);
  const teamCountryNormalized = normalizeRegionCode(teamCountryRaw);

  let effectiveCode = "us";
  let pickedFrom: "user.country" | "team.country" | "default" = "default";
  if (userCountryNormalized) {
    effectiveCode = userCountryNormalized;
    pickedFrom = "user.country";
  } else if (teamCountryNormalized) {
    effectiveCode = teamCountryNormalized;
    pickedFrom = "team.country";
  }

  const endpoint = getAIEndpoint(context);

  return {
    routingEnabled: isAIRegionRoutingEnabled(),
    user: {
      email: rootState.user?.user?.email ?? null,
      countryFromProfile: userCountryRaw,
      countryNormalized: userCountryNormalized,
    },
    team: {
      teamId:
        rootState.team?.team?._id ??
        rootState.user?.user?.teamId ??
        null,
      countryFromTeam: teamCountryRaw,
      countryNormalized: teamCountryNormalized,
    },
    effectiveCountry: {
      code: effectiveCode,
      pickedFrom,
      continent: COUNTRY_TO_CONTINENT[effectiveCode] ?? null,
    },
    endpoint,
  };
}

function readCountryFromStore(): AIEndpointContext {
  const store = getAIEndpointVuexStore();
  if (!store?.state) return {};

  return {
    userCountryCode: readStoredCountry(store.state.user?.user),
    teamCountryCode: readStoredCountry(store.state.team?.team),
  };
}

/** Build routing context from a Vuex action rootState. */
export function aiEndpointContextFromRootState(rootState: {
  user?: {
    user?: { country?: string | null; region?: string | null };
  };
  team?: { team?: { country?: string | null; region?: string | null } };
}): AIEndpointContext {
  return {
    userCountryCode: readStoredCountry(rootState.user?.user),
    teamCountryCode: readStoredCountry(rootState.team?.team),
  };
}

function resolveContext(context?: AIEndpointContext): AIEndpointContext {
  const fromStore = readCountryFromStore();
  return {
    userCountryCode: context?.userCountryCode ?? fromStore.userCountryCode,
    teamCountryCode: context?.teamCountryCode ?? fromStore.teamCountryCode,
  };
}

export function getAIEndpoint(context?: AIEndpointContext): ResolvedAIEndpoint {
  const resolvedContext = resolveContext(context);
  const resolved = getAIEndpointResolved(resolvedContext);
  if (AI_ROUTING_DRY_RUN) {
    logDryRun("resolve", {
      url: resolved.url,
      countryCode: resolved.countryCode,
      source: resolved.source,
      userCountryCode: resolvedContext.userCountryCode ?? null,
      teamCountryCode: resolvedContext.teamCountryCode ?? null,
      routingEnabled: isAIRegionRoutingEnabled(),
    });
  }
  applySentryTags(resolved);
  return resolved;
}

export function getAIEndpointUrl(context?: AIEndpointContext): string {
  return getAIEndpoint(context).url;
}

/** Temporary debug gate: log url/country and throw before any real AI fetch. */
export function throwIfAiRoutingDebugBlocked(source: string): void {
  if (!AI_ROUTING_DRY_RUN) {
    return;
  }

  const store = getAIEndpointVuexStore();
  const lookup = getAIEndpointCountryLookupDebug(store?.state ?? {});
  logDryRun(`${source} blocked`, {
    url: lookup.endpoint.url,
    source: lookup.endpoint.source,
    user: lookup.user,
    team: lookup.team,
    effectiveCountry: lookup.effectiveCountry,
    routingEnabled: lookup.routingEnabled,
  });
  throw new Error(
    `[AI routing debug] ${source} — would fetch: ${lookup.endpoint.url} ` +
      `(user.country=${lookup.user.countryFromProfile ?? "unset"}, ` +
      `team.country=${lookup.team.countryFromTeam ?? "unset"}, ` +
      `effective=${lookup.effectiveCountry.code} / ${lookup.effectiveCountry.pickedFrom})`
  );
}

/** Single HTTP gate for AI proxy — all AI fetches must go through here. */
export function aiFetch(
  url: string,
  init: RequestInit,
  source: string
): Promise<Response> {
  throwIfAiRoutingDebugBlocked(source);
  return fetch(url, init);
}

function applySentryTags(resolved: ResolvedAIEndpoint): void {
  if (!process.env.VUE_APP_SENTRY_DSN) {
    return;
  }
  withSentryBrowser((Sentry) => {
    Sentry.setTag("ai_country", resolved.countryCode);
    Sentry.setTag("ai_routing_source", resolved.source);
  });
}
